import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_USER_BY_ID, UPDATE_USER_MUTATION } from '../../apollo/queries';
import { useAuth } from '../AuthContext';
import '../css/CustomerEditPage.css';

const FormRow = ({ label, children, required, className }) => (
  <div className={`form-row ${className || ''}`}>
    <div className="form-label">
      {label}
      {required && <span className="required">*</span>}
    </div>
    <div className="form-input-container">{children}</div>
  </div>
);

const CustomerEditPage = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('id');
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  const [saving, setSaving] = useState(false);
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);

  // 디버깅을 위한 로그
  console.log('CustomerEditPage - Current user:', currentUser);
  console.log('CustomerEditPage - Is admin:', isAdmin());
  console.log('CustomerEditPage - Target user ID:', userId);

  // 폼 데이터 상태
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    ceoName: '',
    zipCode: '',
    address: '',
    addressDetail: '',
    businessNumber: '',
    email: '',
    phone: '',
    mobile: '',
    department: '',
    fax: '',
    level: 'D_GRADE',
    password: ''  // 비밀번호 필드 추가
  });

  // GraphQL 쿼리 실행
  const { data, loading, error, refetch } = useQuery(GET_USER_BY_ID, {
    variables: { id: parseInt(userId) },
    skip: !userId,
    errorPolicy: 'all',
    onCompleted: (data) => {
      console.log('GET_USER_BY_ID completed:', data);
    },
    onError: (error) => {
      console.error('GET_USER_BY_ID error:', error);
      console.error('GraphQL errors:', error.graphQLErrors);
      console.error('Network error:', error.networkError);
    }
  });

  // 사용자 업데이트 뮤테이션
  const [updateUser] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: (data) => {
      console.log('사용자 정보 업데이트 성공:', data);
      alert('사용자 정보가 성공적으로 업데이트되었습니다.');
      setSaving(false);
      // 비밀번호 필드 초기화
      setFormData(prev => ({ ...prev, password: '' }));
      refetch(); // 데이터 새로고침
    },
    onError: (error) => {
      console.error('사용자 정보 업데이트 실패:', error);
      alert(`업데이트 실패: ${error.message}`);
      setSaving(false);
    }
  });

  // 데이터 로드 시 폼 데이터 설정
  useEffect(() => {
    if (data && data.user) {
      const user = data.user;
      setFormData({
        name: user.name || '',
        company: user.company || '',
        ceoName: user.ceoName || '',
        zipCode: user.zipCode || '',
        address: user.address || '',
        addressDetail: user.addressDetail || '',
        businessNumber: user.businessNumber || '',
        email: user.email || '',
        phone: user.phone || '',
        mobile: user.mobile || '',
        department: user.department || '',
        fax: user.fax || '',
        level: user.level || 'D_GRADE'
      });
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (saving) return;
    
    setSaving(true);
    
    try {
      await updateUser({
        variables: {
          id: parseInt(userId),
          input: {
            name: formData.name,
            company: formData.company,
            ceoName: formData.ceoName,
            zipCode: formData.zipCode,
            address: formData.address,
            addressDetail: formData.addressDetail,
            businessNumber: formData.businessNumber,
            email: formData.email,
            phone: formData.phone,
            mobile: formData.mobile,
            department: formData.department,
            fax: formData.fax,
            level: formData.level,
            ...(formData.password && formData.password.trim() !== '' && { password: formData.password })
          }
        }
      });
    } catch (error) {
      console.error('저장 중 오류:', error);
      setSaving(false);
    }
  };

  // Daum 주소검색 초기화
  useEffect(() => {
    if (isAddressSearchOpen && window.daum && window.daum.Postcode) {
      const element_layer = document.getElementById('address_search_layer');
      
      new window.daum.Postcode({
        oncomplete: function(data) {
          let addr = '';
          let extraAddr = '';

          if (data.userSelectedType === 'R') {
            addr = data.roadAddress;
          } else {
            addr = data.jibunAddress;
          }

          if(data.userSelectedType === 'R'){
            if(data.bname !== '' && /[동|로|가]$/g.test(data.bname)){
              extraAddr += data.bname;
            }
            if(data.buildingName !== '' && data.apartment === 'Y'){
              extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
            }
            if(extraAddr !== ''){
              extraAddr = ' (' + extraAddr + ')';
            }
          }

          setFormData(prev => ({
            ...prev,
            zipCode: data.zonecode,
            address: addr + extraAddr
          }));

          setIsAddressSearchOpen(false);
        },
        onresize: function(size) {
          element_layer.style.height = size.height + 'px';
        },
        width: '100%',
        height: '100%'
      }).embed(element_layer);

      element_layer.style.display = 'block';
    }
  }, [isAddressSearchOpen]);

  const handleAddressSearch = () => {
    if (!window.daum || !window.daum.Postcode) {
      alert('주소 검색 서비스를 불러올 수 없습니다.');
      return;
    }
    setIsAddressSearchOpen(true);
  };

  const closeAddressSearch = () => {
    setIsAddressSearchOpen(false);
    const element_layer = document.getElementById('address_search_layer');
    if (element_layer) {
      element_layer.style.display = 'none';
    }
  };

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="customer-edit-container">
        <div className="breadcrumb">
          <span>홈 &gt; 회원 정보 관리 &gt; 회원 정보 확인 /수정</span>
        </div>
        <div className="page-header">
          <div className="page-title-bar"></div>
          <h2>회원 정보 확인 / 수정</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>고객 정보를 불러오는 중입니다...</div>
        </div>
      </div>
    );
  }

  // 에러 발생 시
  if (error || !data?.user) {
    console.log('Error state:', { error, data, userId });
    return (
      <div className="customer-edit-container">
        <div className="breadcrumb">
          <span>홈 &gt; 회원 정보 관리 &gt; 회원 정보 확인 /수정</span>
        </div>
        <div className="page-header">
          <div className="page-title-bar"></div>
          <h2>회원 정보 확인 / 수정</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
          <div>고객 정보를 찾을 수 없습니다.</div>
          {error && (
            <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
              <div>에러 세부 정보:</div>
              <div>{error.message}</div>
              {error.graphQLErrors?.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  GraphQL 에러: {error.graphQLErrors.map(e => e.message).join(', ')}
                </div>
              )}
              {error.networkError && (
                <div style={{ marginTop: '10px' }}>
                  네트워크 에러: {error.networkError.message}
                </div>
              )}
            </div>
          )}
          <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
            사용자 ID: {userId}
          </div>
        </div>
      </div>
    );
  }

  const userData = data.user;

  return (
      <div className="customer-edit-container">
        <div className="breadcrumb">
          <span>홈 &gt; 회원 정보 관리 &gt; 회원 정보 확인 /수정</span>
        </div>

        <div className="page-header">
          <div className="page-title-bar"></div>
          <h2>회원 정보 확인 / 수정</h2>
        </div>

        <div className="form-section">
          <div className="section-title-container">
            <h3>회사 정보</h3>
            <button className="btn-sm btn-dark">회원 탈퇴</button>
          </div>
          <div className="form-table">
            <FormRow label="대표자명" required>
              <input 
                type="text" 
                name="ceoName"
                value={formData.ceoName}
                onChange={handleInputChange}
                placeholder="예 : 홍길동" 
              />
            </FormRow>
            <FormRow label="회사명" required>
              <input 
                type="text" 
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="예 : 부스바월드" 
              />
            </FormRow>
            <FormRow label="우편번호" required className="multi-field-row">
              <input 
                type="text" 
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                placeholder="우편 번호" 
                className="short-input" 
              />
              <button type="button" className="btn-sm btn-dark" onClick={handleAddressSearch}>주소 검색</button>
            </FormRow>
            <FormRow label="기본 주소" required>
              <input 
                type="text" 
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="주소 검색을 클릭해주세요." 
              />
            </FormRow>
            <FormRow label="상세 주소" required>
              <input 
                type="text" 
                name="addressDetail"
                value={formData.addressDetail}
                onChange={handleInputChange}
                placeholder="상세 주소를 입력해 주세요." 
              />
            </FormRow>
            <FormRow label="사업자 등록 번호" required className="multi-field-row">
              <div className="biz-num-inputs">
                <input 
                  type="text" 
                  name="businessNumber"
                  value={formData.businessNumber}
                  onChange={handleInputChange}
                  placeholder="123-45-67890" 
                />
              </div>
            </FormRow>
          </div>
        </div>

        <div className="form-section">
          <div className="section-title-container">
            <h3>로그인 정보</h3>
          </div>
          <div className="form-table">
            <FormRow label="이메일 주소" required>
              <input 
                type="email" 
                name="email"
                value={formData.email} 
                onChange={handleInputChange}
                placeholder="예 : example@example.com" 
              />
            </FormRow>
            <FormRow label="로그인 ID" required>
              <input 
                type="text" 
                name="email"
                value={formData.email} 
                onChange={handleInputChange}
                className="long-input"
                placeholder="예 : user@example.com"
              />
              <p className="input-hint full-width">이메일 주소가 로그인 ID로 사용됩니다.</p>
            </FormRow>
            <FormRow label="비밀번호 변경">
              <input 
                type="password" 
                name="password"
                value={formData.password} 
                onChange={handleInputChange}
                className="long-input"
                placeholder="새 비밀번호를 입력하세요 (변경하지 않으려면 비워두세요)"
              />
              <p className="input-hint full-width">비밀번호를 변경하려면 새 비밀번호를 입력하세요. 변경하지 않으려면 비워두세요.</p>
            </FormRow>
          </div>
        </div>

        <div className="form-section">
          <div className="section-title-container">
            <h3>사용자 정보</h3>
          </div>
          <div className="form-table">
            <FormRow label="담당자" required>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="예 : 홍길동" 
              />
            </FormRow>
            <FormRow label="부서 / 역할">
              <input 
                type="text" 
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="예 : 구매 담당자" 
              />
            </FormRow>
            <FormRow label="회원 레벨" required>
              <select 
                name="level"
                value={formData.level}
                onChange={handleInputChange}
                className="short-select"
              >
                <option value="D_GRADE">D급</option>
                <option value="C_GRADE">C급</option>
                <option value="B_GRADE">B급</option>
                <option value="A_GRADE">A급</option>
              </select>
            </FormRow>
            <FormRow label="전화번호" required className="multi-field-row">
               <input 
                 type="text" 
                 name="phone"
                 value={formData.phone}
                 onChange={handleInputChange}
                 placeholder="예 : 02-1234-5478" 
               />
               <div className="form-sub-field">
                <div className="form-label">
                  휴대전화<span className="required">*</span>
                </div>
                <input 
                  type="text" 
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="예 : 010-6543-1875" 
                />
              </div>
            </FormRow>
            <FormRow label="FAX">
              <input 
                type="text" 
                name="fax"
                value={formData.fax}
                onChange={handleInputChange}
              />
            </FormRow>
          </div>
        </div>

        <div className="form-section">
          <div className="section-title-container">
            <h3>배송지 정보</h3>
          </div>
          <div className="form-table">
            {userData.deliveryAddresses && userData.deliveryAddresses.length > 0 ? (
              <div className="delivery-addresses-list">
                {userData.deliveryAddresses.map((address, index) => (
                  <div key={address.id} className="delivery-address-item">
                    <div className="address-header">
                      <h4>{address.name || `배송지 ${index + 1}`}</h4>
                      {address.isDefault && <span className="default-badge">기본 배송지</span>}
                    </div>
                    <FormRow label="수령인">
                      <p>{address.recipient}</p>
                    </FormRow>
                    <FormRow label="연락처">
                      <p>{address.phone}</p>
                    </FormRow>
                    <FormRow label="우편번호">
                      <p>{address.zipCode}</p>
                    </FormRow>
                    <FormRow label="주소">
                      <p>{address.address}</p>
                    </FormRow>
                    <FormRow label="상세주소">
                      <p>{address.addressDetail}</p>
                    </FormRow>
                    <FormRow label="등록일">
                      <p>{new Date(address.createdAt).toLocaleDateString('ko-KR')}</p>
                    </FormRow>
                  </div>
                ))}
              </div>
            ) : (
              <FormRow label="배송지 정보">
                <p className="no-data">등록된 배송지가 없습니다.</p>
              </FormRow>
            )}
          </div>
        </div>

        <div className="form-section">
          <div className="section-title-container">
            <h3>활동 정보</h3>
          </div>
          <div className="form-table">
            <FormRow label="회원 가입일">
              <p>{new Date(userData.createdAt).toLocaleDateString('ko-KR')}</p>
            </FormRow>
            <FormRow label="최근 로그인">
              <p>
                {userData.lastLoginAt 
                  ? new Date(userData.lastLoginAt).toLocaleDateString('ko-KR')
                  : '로그인 기록 없음'
                }
              </p>
            </FormRow>
            <FormRow label="견적 횟수">
              <p>{userData.quotations?.length || 0}</p>
            </FormRow>
            <FormRow label="계정 상태">
              <p style={{ color: userData.isActive ? '#28a745' : '#dc3545' }}>
                {userData.isActive ? '활성' : '비활성'}
              </p>
            </FormRow>
            <FormRow label="관리자 메모" className="memo-row">
              <textarea placeholder="관리자 메모를 입력하세요."></textarea>
            </FormRow>
          </div>
        </div>
        
        <div className="page-actions">
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              <span className="arrow-icon">&lt;</span> 돌아가기
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : <>저장 <span className="arrow-icon">&gt;</span></>}
            </button>
        </div>
        
        {/* 주소검색 레이어 */}
        {isAddressSearchOpen && (
          <div className="address-search-overlay">
            <div className="address-search-modal">
              <div className="address-search-header">
                <h3>주소검색</h3>
                <button className="address-search-close" onClick={closeAddressSearch}>×</button>
              </div>
              <div id="address_search_layer" style={{ width: '100%', height: '400px', display: 'none' }}></div>
            </div>
          </div>
        )}
      </div>
  );
};

export default CustomerEditPage; 